import React from 'react';
import styled from 'styled-components';
import { FaCheck } from 'react-icons/fa';
import {
  fadeIn,
  fadeInUp,
  slideInRight,
  gridDrift,
  popIn,
  checkPop,
  reducedMotion,
} from './partnerSignupAnimations';

const Panel = styled.aside`
  position: relative;
  min-height: 520px;
  background: var(--pp-dark);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  animation: ${fadeIn} 0.6s ease 0.15s both;
  ${reducedMotion}

  @media (max-width: 820px) {
    min-height: 240px;
    order: 2;
  }

  @media (max-width: 480px) {
    min-height: 200px;
  }
`;

const GridTexture = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  pointer-events: none;
  animation: ${gridDrift} 20s linear infinite;
  ${reducedMotion}
`;

const BottomFade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, var(--pp-dark) 55%, transparent 100%);
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  padding: 40px 36px;
  color: #ffffff;

  @media (max-width: 820px) {
    padding: 28px 24px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
  }
`;

const TrustBadge = styled.p`
  display: inline-block;
  margin: 0 0 20px 0;
  padding: 6px 14px;
  border: 1px solid var(--pp-brand);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: var(--pp-brand);
  animation: ${popIn} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.3s both;
  ${reducedMotion}

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 5px 12px;
    margin-bottom: 14px;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 24px 0;
  color: #ffffff;
  animation: ${fadeInUp} 0.5s ease 0.4s both;
  ${reducedMotion}

  .accent {
    color: var(--pp-brand);
  }

  @media (max-width: 820px) {
    font-size: 20px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 12px;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 28px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 480px) {
    gap: 10px;
    margin-bottom: 16px;
  }
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--pp-dark-muted);
  animation: ${slideInRight} 0.45s ease ${(p) => 0.45 + p.$delay * 0.12}s both;
  ${reducedMotion}

  strong {
    display: block;
    color: #ffffff;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 2px;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    gap: 10px;

    strong {
      font-size: 13px;
    }
  }
`;

const CheckCircle = styled.span`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid var(--pp-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--pp-brand);
  font-size: 10px;
  margin-top: 2px;
  animation: ${checkPop} 0.35s ease ${(p) => 0.55 + p.$delay * 0.12}s both;
  ${reducedMotion}
`;

const StatsRow = styled.div`
  display: flex;
  align-items: stretch;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
  gap: 0;
  animation: ${fadeInUp} 0.5s ease 0.85s both;
  ${reducedMotion}

  @media (max-width: 480px) {
    padding-top: 14px;
  }
`;

const Stat = styled.div`
  flex: 1;
  text-align: center;
  transition: transform 0.25s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const StatValue = styled.span`
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: var(--pp-brand);
  line-height: 1.2;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const StatLabel = styled.span`
  display: block;
  font-size: 11px;
  color: var(--pp-dark-muted);
  margin-top: 4px;
`;

const StatDivider = styled.div`
  width: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 0 8px;
`;

const FEATURES = [
  {
    title: 'Pan-India reach',
    desc: 'Connect with buyers across the country',
  },
  {
    title: 'GST-compliant invoicing built in',
    desc: null,
  },
  {
    title: 'Real-time order & inventory dashboard',
    desc: null,
  },
];

export default function PartnerSignupHero() {
  return (
    <Panel>
      <GridTexture aria-hidden="true" />
      <BottomFade aria-hidden="true" />
      <Content>
        <TrustBadge>Trusted by 10,000+ sellers &amp; garages</TrustBadge>
        <Title>
          Grow your <span className="accent">automotive</span> business online
        </Title>
        <FeatureList>
          {FEATURES.map(({ title, desc }, index) => (
            <FeatureItem key={title} $delay={index}>
              <CheckCircle $delay={index}><FaCheck /></CheckCircle>
              <div>
                <strong>{title}</strong>
                {desc && <span>{desc}</span>}
              </div>
            </FeatureItem>
          ))}
        </FeatureList>
        <StatsRow>
          <Stat>
            <StatValue>10K+</StatValue>
            <StatLabel>Partners</StatLabel>
          </Stat>
          <StatDivider />
          <Stat>
            <StatValue>500+</StatValue>
            <StatLabel>Cities</StatLabel>
          </Stat>
          <StatDivider />
          <Stat>
            <StatValue>24/7</StatValue>
            <StatLabel>Support</StatLabel>
          </Stat>
        </StatsRow>
      </Content>
    </Panel>
  );
}
